import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnPreviewStrip } from "./ColumnPreviewStrip";
import { EmailBuilderSettingsFieldGrid, EmailBuilderSettingsFieldGridRow } from "./controls/EmailBuilderSettingsFieldGrid";
import { EmailBuilderSettingsIconNumberField } from "./controls/EmailBuilderSettingsIconNumberField";
import { resolveRowLayoutWidth } from "../domain/block-width";
import {
  columnMaxWidthPercent,
  resolveRowColumnMinWidthPercents,
  rowHasColumnBlockOverflow,
} from "../domain/column-content-min";
import { EmailBuilderRowColumnOverflowHint } from "./EmailBuilderColumnOverflowHint";
import {
  layoutPresetPercentsWithMins,
  matchColumnLayoutPreset,
  MIN_COLUMN_PERCENT,
  rebalancePercents,
  type ColumnLayoutPreset,
} from "../domain/column-presets";
import { findRow } from "../domain/find";
import { selectDocument } from "../domain/selectors";
import { useEditorActions, useEditorStore } from "../domain/redux/hooks";

const LAYOUT_PRESET_OPTIONS: Array<{
  id: ColumnLayoutPreset;
  labelKey: string;
  defaultLabel: string;
}> = [
  { id: 'equal', labelKey: 'Equal', defaultLabel: 'Equal' },
  { id: 'wide-left', labelKey: 'Wide left', defaultLabel: 'Wide left' },
  { id: 'wide-right', labelKey: 'Wide right', defaultLabel: 'Wide right' },
  { id: 'custom', labelKey: 'Custom', defaultLabel: 'Custom' },
];

export function ColumnLayoutSection({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const { setColumnWidths } = useEditorActions();
  const row = findRow(document, rowId);
  const columnCount = row?.columns.length ?? 0;
  const columnWidths = row?.columnWidths ?? [];
  const innerWidth = row
    ? resolveRowLayoutWidth(document, rowId)?.innerWidth ?? 0
    : 0;
  const minPercents = row
    ? resolveRowColumnMinWidthPercents(
        row,
        innerWidth,
        document.settings.bodyFontFamily,
      )
    : [];
  const hasColumnOverflow =
    row !== undefined &&
    innerWidth > 0 &&
    rowHasColumnBlockOverflow(
      row,
      innerWidth,
      document.settings.bodyFontFamily,
    );

  const [chosenPreset, setChosenPreset] = useState<ColumnLayoutPreset | null>(null);

  useEffect(() => {
    setChosenPreset(null);
  }, [rowId, columnCount]);

  useEffect(() => {
    if (
      chosenPreset === null ||
      chosenPreset === 'custom' ||
      columnCount <= 1
    ) {
      return;
    }

    const expected = layoutPresetPercentsWithMins(
      chosenPreset,
      columnCount,
      minPercents,
    );
    const stillMatchesChosen =
      expected.length === columnWidths.length &&
      expected.every((width, index) => width === columnWidths[index]);

    if (!stillMatchesChosen) {
      setChosenPreset('custom');
    }
  }, [chosenPreset, columnCount, columnWidths, minPercents]);

  const columnWidthFieldRows = useMemo(() => {
    if (!row || row.columns.length <= 1) {
      return [];
    }

    const fields = row.columns.map((column, index) => {
      const min = minPercents[index] ?? MIN_COLUMN_PERCENT;
      const max = columnMaxWidthPercent(minPercents, index);
      return {
        key: column.id,
        label: t("Column {{number}} (%)", { number: index + 1 }),
        value: row.columnWidths[index] ?? MIN_COLUMN_PERCENT,
        min,
        max,
        disabled: max < min,
        index,
      };
    });

    const rows: typeof fields[] = [];
    for (let index = 0; index < fields.length; index += 2) {
      rows.push(fields.slice(index, index + 2));
    }
    return rows;
  }, [minPercents, row, t]);

  if (!row) {
    return null;
  }

  const showWidthControls = columnCount > 1;
  const matchedPreset =
    columnCount > 1
      ? matchColumnLayoutPreset(columnWidths, columnCount)
      : 'equal';
  const activePreset = chosenPreset ?? matchedPreset;

  function applyPreset(preset: Exclude<ColumnLayoutPreset, 'custom'>) {
    setColumnWidths(
      rowId,
      layoutPresetPercentsWithMins(preset, columnCount, minPercents),
    );
    setChosenPreset(preset);
  }

  function updateColumnWidth(index: number, width: number) {
    setChosenPreset('custom');
    setColumnWidths(
      rowId,
      rebalancePercents(columnWidths, index, width, minPercents),
    );
  }

  return (
    <div className={"email-builder-settings-column-layout"}>
      {showWidthControls ? (
        <div className={"email-builder-settings-segmented-field"}>
          <p className="email-builder-settings-field-label">{t("Column widths")}</p>
          <div
            className={
              "email-builder-settings-segment-wide email-builder-settings-segment-wide--column-widths"
            }
            role="group"
            aria-label={t("Column layout")}
          >
            {LAYOUT_PRESET_OPTIONS.map(option => (
              <button
                key={option.id}
                type="button"
                aria-pressed={activePreset === option.id}
                data-on={activePreset === option.id ? 'true' : 'false'}
                onClick={() => {
                  if (option.id === 'custom') {
                    setChosenPreset('custom');
                    return;
                  }
                  applyPreset(option.id);
                }}
              >
                {t(option.labelKey, option.defaultLabel)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {showWidthControls ? <ColumnPreviewStrip rowId={rowId} /> : null}

      {showWidthControls && hasColumnOverflow ? (
        <EmailBuilderRowColumnOverflowHint />
      ) : null}

      {showWidthControls ? (
        <EmailBuilderSettingsFieldGrid>
          {columnWidthFieldRows.map(rowFields => (
            <EmailBuilderSettingsFieldGridRow key={rowFields.map(field => field.key).join("-")}>
              {rowFields.map(field => (
                <EmailBuilderSettingsIconNumberField
                  key={field.key}
                  label={field.label}
                  value={field.value}
                  min={field.min}
                  max={field.max}
                  disabled={field.disabled}
                  onChange={width => {
                    updateColumnWidth(field.index, width);
                  }}
                />
              ))}
            </EmailBuilderSettingsFieldGridRow>
          ))}
        </EmailBuilderSettingsFieldGrid>
      ) : null}
    </div>
  );
}
